import React, {
  Component,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { List } from 'immutable';
import { createFile } from '@kineticdata/bundle-common';
import { Editor, Viewer } from '@toast-ui/react-editor';
import axios from 'axios';
const localAxios = axios.create({ withCredentials: false });

// Constants for accessing the files in S3
const CM_IMAGE_FILESTORE_SLUG = 'service-catalog-content';
const CM_IMAGE_S3_BUCKET_NAME = 'erms-service-catalog-content';
const CM_IMAGE_S3_BASE_PATH = 'https://s3.amazonaws.com';

// React element that handles custom image upload to S3
const ImagePopupBodyComponent = ({ editor, imageDir }) => {
  // State for existing images
  const [images, setImages] = useState({
    data: List(),
    loading: false,
    error: null,
  });
  // State for insert image popup
  const [alt, setAlt] = useState('');
  const [selected, setSelected] = useState(null);
  const [file, setFile] = useState(null);
  const [fileDataURL, setFileDataURL] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Reset function for resetting popup state. If the first parameter is true,
  // the popup will not be closed, thus taking the user to the initial state of
  // the popup. Otherwise the popup will close.
  const reset = useCallback(
    keepOpen => {
      if (keepOpen !== true) {
        editor.eventEmitter.emit('closePopup');
      }
      setAlt('');
      setSelected(null);
      setFile(null);
      setFileDataURL(null);
      setUploadError(null);
      setUploading(false);
    },
    [editor.eventEmitter],
  );

  // Define a callback for fetching images from S3 and parsing the response
  const fetchImages = useCallback(
    () => {
      setImages({
        data: List(),
        loading: !!imageDir,
        error: !imageDir
          ? 'The image directory is not properly configured. Please contact an administrator.'
          : null,
      });
      if (!!imageDir) {
        localAxios
          .get(
            `${CM_IMAGE_S3_BASE_PATH}/${CM_IMAGE_S3_BUCKET_NAME}?list-type=2&prefix=${imageDir}`,
          )
          .then(response => {
            const doc = new DOMParser().parseFromString(
              response.data,
              'application/xml',
            );
            const data = Array.from(
              doc.getElementsByTagName('Contents'),
              o =>
                Array.from(
                  o.getElementsByTagName('Key'),
                  k => k.childNodes[0].nodeValue,
                )[0],
            ).filter(img => !!img && !img.endsWith('/'));
            setImages({
              data: List(data || []),
              loading: false,
              error: null,
            });
          })
          .catch(e => {
            console.error(e);
            setImages({
              data: List(),
              loading: false,
              error: 'Failed to connect to image storage.',
            });
          });
      }
    },
    [imageDir],
  );

  // Fetch images on load of the component and anytime the fetchImages function
  // changes, which will happend when the imageDir changes.
  useEffect(
    () => {
      fetchImages();
    },
    [fetchImages],
  );

  if (!!images.error) {
    // If we can't fetch the images from S3, then show an error instead of the
    // insert image functionality since we likely can't connect to S3.
    return (
      <div className="alert alert-bar alert-danger m-0">{images.error}</div>
    );
  } else if (!!uploadError) {
    // If there's an error while uploading a new image, show the error with the
    // option to reset the popup.
    return (
      <div>
        <div className="heading">Upload Error</div>
        <div className="alert alert-bar alert-danger m-0">{uploadError}</div>
        <div className="toastui-editor-button-container">
          <button
            className="toastui-editor-close-button float-left"
            onClick={() => reset(true)}
          >
            <span className="fa fa-arrow-left fa-fw" />
            Back
          </button>
          <button className="toastui-editor-close-button mr-0" onClick={reset}>
            Cancel
          </button>
        </div>
      </div>
    );
  } else if (!!selected) {
    // If an existing image was selected, show the image and an input for alt
    // text, with options to go back, cancel, or insert the image.
    return (
      <div>
        <div className="heading">Selected Image</div>
        <div className="selected-image-container">
          <label>{selected.replace(`${imageDir}/`, '')}</label>
          <img
            src={`${CM_IMAGE_S3_BASE_PATH}/${CM_IMAGE_S3_BUCKET_NAME}/${selected}`}
            alt=""
          />
        </div>
        <div>
          <label htmlFor="s3-image-alt">Description</label>
          <input
            type="text"
            id="s3-image-alt"
            value={alt}
            onChange={e => setAlt(e.target.value)}
          />
        </div>
        <div className="toastui-editor-button-container">
          <button
            className="toastui-editor-close-button float-left"
            onClick={() => reset(true)}
          >
            <span className="fa fa-arrow-left fa-fw" />
            Back
          </button>
          <button className="toastui-editor-close-button" onClick={reset}>
            Cancel
          </button>
          <button
            className="toastui-editor-ok-button"
            onClick={() => {
              editor.exec('addImage', {
                imageUrl: `${CM_IMAGE_S3_BASE_PATH}/${CM_IMAGE_S3_BUCKET_NAME}/${selected}`,
                altText: alt,
              });
              reset();
            }}
          >
            OK
          </button>
        </div>
      </div>
    );
  } else if (!!file) {
    // If a new file is being uploaded, show the image and an input for alt
    // text, with options to go back, cancel, or upload and insert the image.
    return (
      <div>
        <div className="heading">Upload New Image</div>
        <div className="selected-image-container">
          <label>{file.name}</label>
          {fileDataURL && <img src={fileDataURL} alt="" />}
        </div>
        <label htmlFor="s3-image-alt">Description</label>
        <input
          type="text"
          id="s3-image-alt"
          value={alt}
          onChange={e => setAlt(e.target.value)}
        />
        <div className="toastui-editor-button-container">
          <button
            className="toastui-editor-close-button float-left"
            onClick={() => reset(true)}
            disabled={uploading}
          >
            <span className="fa fa-arrow-left fa-fw" />
            Back
          </button>
          <button
            className="toastui-editor-close-button"
            onClick={reset}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            className="toastui-editor-ok-button"
            onClick={() => {
              setUploading(true);
              // Use a file resource to upload the image to S3
              createFile({
                slug: CM_IMAGE_FILESTORE_SLUG,
                path: imageDir,
                file,
              }).then(({ data, error }) => {
                if (error) {
                  setUploadError(error.message || 'Upload failed');
                } else {
                  editor.exec('addImage', {
                    imageUrl: `${CM_IMAGE_S3_BASE_PATH}/${CM_IMAGE_S3_BUCKET_NAME}/${
                      data.path
                    }`,
                    altText: alt,
                  });
                  fetchImages();
                  reset();
                }
              });
            }}
            disabled={uploading}
          >
            {!uploading ? (
              'OK'
            ) : (
              <span className="fa fa-spinner fa-spin fa-fw" />
            )}
          </button>
        </div>
      </div>
    );
  } else {
    // Show a list of existing images the user can select to insert and an
    // option to upload a new image.
    return (
      <div>
        <div className="heading">Existing Images</div>
        {images.data.size > 0 ? (
          <div className="existing-images-container">
            {images.data.map(image => (
              <button key={image} onClick={() => setSelected(image)}>
                {image.replace(`${imageDir}/`, '')}
              </button>
            ))}
          </div>
        ) : (
          <div className="alert alert-bar alert-light m-0">
            There are no existing images.
          </div>
        )}
        <div className="toastui-editor-button-container">
          <button className="toastui-editor-close-button" onClick={reset}>
            Cancel
          </button>
          <label htmlFor="s3-image-input" className="upload-image-button">
            <span>Upload New Image</span>
            <input
              type="file"
              accept="image/*"
              autoComplete="off"
              id="s3-image-input"
              onChange={e => {
                const uploadedFile = e.target.files[0];
                if (uploadedFile) {
                  setFile(uploadedFile);
                  const reader = new FileReader();
                  reader.onload = function(e) {
                    setFileDataURL(this.result);
                  };
                  reader.readAsDataURL(uploadedFile);
                }
              }}
            />
          </label>
        </div>
      </div>
    );
  }
};

// Export a customized version of the Markdown component to be used by the
// content manager renderers.
export const Markdown = ({ imageDir, ...props }) => {
  // Use a ref to store the dom element that will have the impage popup
  // component rendered inside it.
  const imagePopupBodyEl = useRef();

  return (
    <MarkdownCommon
      editorProps={{
        minHeight: '400px',
        toolbarItems: [
          ['heading', 'bold', 'italic', 'strike'],
          ['link', 'table', 'hr'],
          ['ul', 'ol', 'task', 'indent', 'outdent'],
          ['quote', 'code', 'codeblock'],
        ],
      }}
      customHTMLRenderer={{
        table(node, { entering }) {
          return {
            type: entering ? 'openTag' : 'closeTag',
            tagName: 'table',
            classNames: ['table', 'table--settings'],
          };
        },
      }}
      onEditorMount={editor => {
        // Create the dom element that gets passed to the editor for rendering
        // the insert image popup.
        imagePopupBodyEl.current = document.createElement('div');
        imagePopupBodyEl.current.classList.add('toastui-editor-s3-image-popup');
        // Render image popup React app into the above dom element
        ReactDOM.render(
          <ImagePopupBodyComponent editor={editor} imageDir={imageDir} />,
          imagePopupBodyEl.current,
        );
        // Insert image toolbar item with custom functionality
        editor.insertToolbarItem(
          { groupIndex: 1, itemIndex: 0 },
          {
            name: 'image',
            tooltip: 'Insert image',
            popup: {
              body: imagePopupBodyEl.current,
            },
            className: 'toastui-editor-toolbar-icons image',
          },
        );
      }}
      onEditorUnmount={editor => {
        // Unmount the image popup React app from the dom element.
        ReactDOM.unmountComponentAtNode(imagePopupBodyEl.current);
        imagePopupBodyEl.current = null;
      }}
      viewerProps={['customHTMLRenderer']}
      {...props}
    />
  );
};

/**
 * Custom version of the Markdown component from @kineticdata/bundle-common
 * that adds a configureEditor function to allow for modifying the editor when
 * it is first initialized.
 */
class MarkdownCommon extends Component {
  constructor(props) {
    super(props);
    this.editor = null;
    this.setEditorRef = element => {
      if (element && element.getInstance) {
        if (typeof props.onEditorMount === 'function' && !props.disabled) {
          props.onEditorMount(element.getInstance());
        }
      } else if (this.editor && this.editor.getInstance) {
        if (typeof props.onEditorUnmount === 'function' && !props.disabled) {
          props.onEditorUnmount(this.editor.getInstance());
        }
      }
      this.editor = element;
    };
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  handleChange = () => {
    if (typeof this.props.onChange === 'function') {
      this.props.onChange(this.editor.getInstance().getMarkdown());
    }
  };

  render() {
    const {
      initialValue,
      onChange,
      disabled,
      className,
      onEditorMount,
      onEditorUnmount,
      viewerProps = {},
      editorProps = {},
      ...otherProps
    } = this.props;
    if (!this.state.hasError) {
      return (
        <div
          className={classNames(className, {
            'markdown-editor': !disabled,
            'markdown-viewer': !!disabled,
          })}
        >
          {!disabled ? (
            <Editor
              height="auto"
              initialEditType="wysiwyg"
              {...editorProps}
              {...otherProps}
              ref={this.setEditorRef}
              initialValue={initialValue}
              onChange={this.handleChange}
            />
          ) : (
            <Viewer
              height="auto"
              linkAttribute={{
                target: '_blank',
                contenteditable: 'false',
                rel: 'noopener noreferrer',
              }}
              {...viewerProps}
              {...otherProps}
              initialValue={initialValue}
            />
          )}
        </div>
      );
    } else {
      return (
        <div>
          <strong className="d-block text-danger">
            There was an error loading the Markdown{' '}
            {!disabled ? 'Editor' : 'Viewer'}, likely due to invalid markdown.
            Falling back to raw {!disabled ? 'edit' : 'view'} mode.
          </strong>
          {!disabled ? (
            <RawMarkdownEditor
              initialValue={initialValue}
              onChange={onChange}
            />
          ) : (
            <div className="form-control-plaintext h-100 pre">{initialValue}</div>
          )}
        </div>
      );
    }
  }
}

class RawMarkdownEditor extends Component {
  constructor(props) {
    super(props);
    this.state = { value: props.initialValue };
  }

  handleChange = e => {
    const value = e.target.value;
    this.setState({ value });
    this.props.onChange(value);
  };

  render() {
    return (
      <textarea
        style={{ height: '70vh' }}
        className="form-control"
        value={this.state.value}
        onChange={this.handleChange}
      />
    );
  }
}
